import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadUser } from '../redux/slices/authSlice';
import { cancelSubscription } from '../redux/slices/subscriptionSlice';
import axios from 'axios';
import toast from 'react-hot-toast';
import { User, Mail, Building, Phone, CreditCard, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const Profile = () => {
  const { user, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  
  const [formData, setFormData] = useState({
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    company: user?.profile?.company || '',
    phone: user?.profile?.phone || '',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';
      await axios.put(
        `${API_URL}/users/profile`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      toast.success('Profile updated successfully');
      dispatch(loadUser());
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      try {
        await dispatch(cancelSubscription()).unwrap();
        toast.success('Subscription cancelled successfully');
        dispatch(loadUser());
      } catch (error) {
        toast.error('Failed to cancel subscription');
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account information</p>
      </div>

      {/* Profile Information */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Personal Information</h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn btn-secondary"
            >
              Edit Profile
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={!isEditing}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={!isEditing}
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-1" />
              Email Address
            </label>
            <input
              type="email"
              value={user?.email}
              disabled
              className="input bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4 inline mr-1" />
              Company
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              disabled={!isEditing}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-1" />
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={!isEditing}
              className="input"
            />
          </div>

          {isEditing && (
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    firstName: user?.profile?.firstName || '',
                    lastName: user?.profile?.lastName || '',
                    company: user?.profile?.company || '',
                    phone: user?.profile?.phone || '',
                  });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Subscription Information */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-6">
          <CreditCard className="w-5 h-5 inline mr-2" />
          Subscription
        </h2>

        {user?.subscription?.status === 'active' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-gray-600">Current Plan</span>
              <span className="font-medium">{user.subscription.plan_id?.name}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-gray-600">Status</span>
              <span className="badge badge-success">Active</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-gray-600">
                <Calendar className="w-4 h-4 inline mr-1" />
                Renews On
              </span>
              <span className="font-medium">
                {user.subscription.current_period_end 
                  ? format(new Date(user.subscription.current_period_end), 'MMM dd, yyyy')
                  : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-gray-600">Price</span>
              <span className="font-medium">
                ${user.subscription.plan_id?.price}/{user.subscription.plan_id?.interval}
              </span>
            </div>

            <button
              onClick={handleCancelSubscription}
              className="btn btn-danger w-full mt-4"
            >
              Cancel Subscription
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">You are currently on the free plan</p>
            <a href="/subscriptions" className="btn btn-primary">
              Upgrade Plan
            </a>
          </div>
        )}
      </div>

      {/* Account Statistics */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Account Statistics</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-3xl font-bold text-gray-900">{user?.invoiceCount || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Invoices Created</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-3xl font-bold text-gray-900">
              {user?.createdAt ? format(new Date(user.createdAt), 'MMM yyyy') : '-'}
            </p>
            <p className="text-sm text-gray-600 mt-1">Member Since</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

